import { useId, useMemo, useState } from 'react'
import { AuditPeriodPicker } from '../AuditPeriodPicker'
import type { AuditEvent } from '../api'
import { endOfYmdMs, normalizeRange, startOfYmdMs } from '../periodUtils'

function parseAuditTime(createdAt: string): number {
	const s = createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T')
	const t = Date.parse(s)
	return Number.isNaN(t) ? 0 : t
}

type AuditSortKey = 'created_at' | 'login' | 'event_type' | 'page_slug' | 'ip'

const PAGE_EMPTY = '__empty__'

function compareAudit(a: AuditEvent, b: AuditEvent, key: AuditSortKey): number {
	switch (key) {
		case 'created_at':
			return parseAuditTime(a.created_at) - parseAuditTime(b.created_at)
		case 'login':
			return (a.login ?? '').localeCompare(b.login ?? '', 'ru')
		case 'event_type':
			return a.event_type.localeCompare(b.event_type, 'ru')
		case 'page_slug':
			return (a.page_slug ?? '').localeCompare(b.page_slug ?? '', 'ru')
		case 'ip':
			return a.ip.localeCompare(b.ip, 'ru')
		default:
			return 0
	}
}

export function AuditEventsView({
	events,
	showLoginColumn,
	variant = 'page',
}: {
	events: AuditEvent[]
	showLoginColumn: boolean
	variant?: 'page' | 'modal'
}) {
	const uid = useId()
	const [periodFrom, setPeriodFrom] = useState('')
	const [periodTo, setPeriodTo] = useState('')
	const [pageFilter, setPageFilter] = useState('')
	const [sortKey, setSortKey] = useState<AuditSortKey>('created_at')
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

	const pageOptions = useMemo(() => {
		const slugs = new Set<string>()
		for (const e of events) {
			slugs.add(e.page_slug ?? '')
		}
		return [...slugs].sort((x, y) => {
			const ax = x === '' ? '\uFFFF' : x
			const ay = y === '' ? '\uFFFF' : y
			return ax.localeCompare(ay, 'ru')
		})
	}, [events])

	const filteredSorted = useMemo(() => {
		let rows = [...events]
		const { from: pf, to: pt } = normalizeRange(periodFrom, periodTo)
		if (pf) {
			const t0 = startOfYmdMs(pf)
			if (t0 != null) {
				rows = rows.filter((e) => parseAuditTime(e.created_at) >= t0)
			}
		}
		if (pt) {
			const t1 = endOfYmdMs(pt)
			if (t1 != null) {
				rows = rows.filter((e) => parseAuditTime(e.created_at) <= t1)
			}
		}
		if (pageFilter === PAGE_EMPTY) {
			rows = rows.filter((e) => e.page_slug == null || e.page_slug === '')
		} else if (pageFilter !== '') {
			rows = rows.filter((e) => e.page_slug === pageFilter)
		}
		rows.sort((a, b) => {
			const c = compareAudit(a, b, sortKey)
			return sortDir === 'asc' ? c : -c
		})
		return rows
	}, [events, pageFilter, periodFrom, periodTo, sortDir, sortKey])

	const toggleSort = (key: AuditSortKey) => {
		if (sortKey === key) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortKey(key)
			setSortDir(key === 'created_at' ? 'desc' : 'asc')
		}
	}

	const sortableHeader = (key: AuditSortKey, label: string) => {
		const ind = sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : ''
		return (
			<th scope="col">
				<button type="button" className={`th-sort${sortKey === key ? ' is-active' : ''}`} onClick={() => toggleSort(key)}>
					{label}
					{ind ? <span className="sort-ind">{ind}</span> : null}
				</button>
			</th>
		)
	}

	const rootClass = variant === 'modal' ? 'audit-events-view audit-events-view--modal' : 'audit-events-view'

	return (
		<div className={rootClass}>
			<div className="audit-toolbar">
				<div className="audit-filters">
					<div className="field field-tight audit-filter-period">
						<AuditPeriodPicker
							id={`${uid}-period`}
							label="Период"
							dateFrom={periodFrom}
							dateTo={periodTo}
							onChange={(f, t) => {
								setPeriodFrom(f)
								setPeriodTo(t)
							}}
						/>
					</div>
					<div className="field field-tight">
						<label htmlFor={`${uid}-page`}>Страница</label>
						<select id={`${uid}-page`} value={pageFilter} onChange={(e) => setPageFilter(e.target.value)}>
							<option value="">Все</option>
							{pageOptions.some((p) => p === '') ? (
								<option value={PAGE_EMPTY}>(нет страницы)</option>
							) : null}
							{pageOptions
								.filter((p) => p !== '')
								.map((p) => (
									<option key={p} value={p}>
										{p}
									</option>
								))}
						</select>
					</div>
				</div>
				<p className="audit-meta muted">
					Показано: {filteredSorted.length} из {events.length}
				</p>
			</div>
			<div className="audit-table-wrap">
				<table>
					<thead>
						<tr>
							{sortableHeader('created_at', 'Время')}
							{showLoginColumn ? sortableHeader('login', 'Логин') : null}
							{sortableHeader('event_type', 'Событие')}
							{sortableHeader('page_slug', 'Страница')}
							{sortableHeader('ip', 'IP')}
						</tr>
					</thead>
					<tbody>
						{filteredSorted.length === 0 ? (
							<tr>
								<td colSpan={showLoginColumn ? 5 : 4} className="muted">
									Нет записей по выбранным условиям
								</td>
							</tr>
						) : (
							filteredSorted.map((event) => (
								<tr key={event.id}>
									<td>{event.created_at}</td>
									{showLoginColumn ? <td>{event.login ?? '-'}</td> : null}
									<td>{event.event_type}</td>
									<td>{event.page_slug ?? '-'}</td>
									<td>{event.ip}</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	)
}
